/* ==========================================================================
   SIGNATURE SECTIONS
   Shop the look and Recently viewed.

   Requested by the two sections themselves rather than loaded on every page,
   because neither appears on more than one template. Both elements are
   self-contained: nothing here reaches into theme.js beyond the optional
   reveal observer, so the load order of the bundles cannot matter.
   ========================================================================== */

(function () {
  'use strict';

  // A section can appear twice on one page, and each copy asks for this file.
  // Defining an element twice is a hard error, so every define is guarded.
  function define(name, constructor) {
    if (!customElements.get(name)) customElements.define(name, constructor);
  }

  /* ========================================================================
     <shop-the-look> — hotspots pinned to an editorial image.

     Every panel is already in the DOM, so selecting a piece is a class swap
     rather than a fetch. The dots behave as one composite control: Tab enters
     the group once and the arrow keys move between pieces, which is both the
     expected pattern and far less tabbing than eight separate stops.
     ======================================================================== */

  class ShopTheLook extends HTMLElement {
    connectedCallback() {
      this.dots = Array.from(this.querySelectorAll('[data-stl-dot]'));
      this.panels = Array.from(this.querySelectorAll('[data-stl-panel]'));
      if (this.dots.length === 0) return;

      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', () => this.select(index));
        dot.addEventListener('keydown', (event) => this.onKeyDown(event, index));
      });

      this.active = 0;
      this.syncTabStops();
    }

    onKeyDown(event, index) {
      const forward = event.key === 'ArrowRight' || event.key === 'ArrowDown';
      const back = event.key === 'ArrowLeft' || event.key === 'ArrowUp';
      if (!forward && !back) return;

      event.preventDefault();
      const step = forward ? 1 : -1;
      const next = (index + step + this.dots.length) % this.dots.length;
      this.select(next);
      this.dots[next].focus();
    }

    select(index) {
      if (index === this.active) return;
      this.active = index;

      this.dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === index);
        dot.setAttribute('aria-expanded', i === index ? 'true' : 'false');
      });

      this.panels.forEach((panel, i) => {
        panel.classList.toggle('is-active', i === index);
        panel.hidden = i !== index;
      });

      this.syncTabStops();
    }

    // One tab stop for the whole group; the arrow keys do the rest.
    syncTabStops() {
      this.dots.forEach((dot, i) => {
        dot.tabIndex = i === this.active ? 0 : -1;
      });
    }
  }

  define('shop-the-look', ShopTheLook);

  /* ========================================================================
     <recently-viewed> — a local trail, re-rendered from the Search API.

     The trail lives in localStorage, so it needs no account and no cookie
     banner. Only the handles are stored: the cards themselves are fetched
     back from Shopify, so a price change or a sell-out shows up immediately
     instead of being frozen at the moment the shopper looked.
     ======================================================================== */

  const RECENT_KEY = 'theme:recently-viewed';
  const RECENT_MAX = 12;

  function readTrail() {
    try {
      const raw = localStorage.getItem(RECENT_KEY);
      const list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) ? list.filter((item) => typeof item === 'string') : [];
    } catch (error) {
      return []; // Storage blocked — private mode, or the shopper turned it off.
    }
  }

  function record(handle) {
    if (!handle) return;
    try {
      const next = [handle].concat(readTrail().filter((item) => item !== handle));
      localStorage.setItem(RECENT_KEY, JSON.stringify(next.slice(0, RECENT_MAX)));
    } catch (error) {
      /* Storage unavailable — the section simply never appears. */
    }
  }

  class RecentlyViewed extends HTMLElement {
    connectedCallback() {
      this.rail = this.querySelector('[data-recently-viewed-rail]');
      if (!this.rail) return;

      // `data-exclude` is only set on a product page, which makes it both the
      // handle to record and the one to leave out of the rail below it.
      const current = this.dataset.exclude;
      if (current) record(current);

      const limit = parseInt(this.dataset.limit, 10) || 6;
      const handles = readTrail()
        .filter((handle) => handle !== current)
        .slice(0, limit);

      if (handles.length === 0) return;
      this.render(handles);
    }

    async render(handles) {
      const params = new URLSearchParams({
        q: handles.map((handle) => 'handle:' + handle).join(' OR '),
        type: 'product',
        section_id: this.dataset.sectionId,
      });

      let markup;
      try {
        const response = await fetch(`${window.routes.search}?${params}`);
        if (!response.ok) return;
        markup = await response.text();
      } catch (error) {
        return; // Offline or blocked — leave the section hidden.
      }

      const fetched = new DOMParser()
        .parseFromString(markup, 'text/html')
        .querySelector('[data-recently-viewed-results]');
      if (!fetched) return;

      // Search answers by relevance; the shopper expects their own order,
      // most recently viewed first.
      const byHandle = new Map(
        Array.from(fetched.querySelectorAll('[data-handle]')).map((node) => [
          node.dataset.handle,
          node,
        ])
      );

      const ordered = handles.map((handle) => byHandle.get(handle)).filter(Boolean);
      if (ordered.length === 0) return;

      ordered.forEach((node) => this.rail.append(node.firstElementChild || node));
      this.hidden = false;

      if (window.Theme && window.Theme.observeReveals) window.Theme.observeReveals(this);
    }
  }

  define('recently-viewed', RecentlyViewed);
})();
